<template>
	<div
		class="sidebar-cart"
		:class="{
			'sidebar-cart--inline': isInline,
			'sidebar-cart--outside': !isInline,
			'sidebar-cart--restricted': hasFeature('FEATURE_GROUP_ORDERS') && cartIsGroupOrder && !groupOrderIsOwner,
		}"
	>
		<div
			class="sidebar-cart__wrapper"
			:class="{
				'sidebar-cart--editable': isEditableEnhanced,
			}"
		>
			<div class="sidebar-cart__inner">
				<div class="sidebar-cart__title">
          <div class="sidebar-cart__title-wrapper">
            <div
              v-if="cartIsUberDirect"
              class="sidebar-cart__partner"
            >
              <div
                class="sidebar-cart__partner-text"
                v-html="$t('uberDirect.deliveryBy')"
              />
              <SVGUberDirect class="sidebar-cart__partner-logo" />
              <div
                v-if="!cartHasAddress"
                class="sidebar-cart__partner-text"
                v-html="$t('uberDirect.deliveryAtCheckout')"
              />
            </div>
            <ul class="sidebar-cart__info">
              <!-- TODO: make this dynamic and/or use translation -->
              <li class="sidebar-cart__info-item">
                <component
                  class="sidebar-cart__info-item-icon"
                  v-if="shippingMethod && shippingMethod.code"
                  :is="shippingMethodIconComponent(shippingMethod.code)"
                />
                <span
                  v-if="shippingMethod"
                  v-html="shippingMethod.name"
                  class="sidebar-cart__info-item-text"
                />
              </li>
              <li class="sidebar-cart__info-item">
                <SVGLocation
                  class="sidebar-cart__info-item-icon"
                />
                <span
                  v-if="displayLocation"
                  v-html="displayLocation"
                  class="sidebar-cart__info-item-text">
                </span>
                <Button
                  v-if="!isIndining && !cartIsGroupOrder"
                  class="sidebar-cart__info-item-edit"
                  icon="edit"
                  type="button"
                  level="tertiary"
                  @click.prevent="onLocationEdit"
                />
              </li>
              <li
                class="sidebar-cart__info-item"
                v-if="deliveryTimeEnhanced && !timeDisabled"
              >
                <SVGClock
                  class="sidebar-cart__info-item-icon"
                />
                <span
                  class="sidebar-cart__info-item-text"
                  v-html="deliveryTimeEnhanced"
                />
                <Button
                  v-if="!isIndining && !timeLocked"
                  class="sidebar-cart__info-item-edit"
                  icon="edit"
                  type="button"
                  level="tertiary"
                  @click.prevent="onTimeEdit"
                />
              </li>
            </ul>
          </div>

					<button
						class="sidebar-cart__title-close"
						@click="close"
					>
						<SVGClose/>
					</button>
				</div>

				<div
					class="sidebar-cart__continue"
					v-if="showContinue"
				>
					<Button
						class="sidebar-cart__continue-button"
						@click.prevent="backToShop"
						:text="$t('cart.continueShopping')"
						level="tertiary"
						type="button"
						:arrow="true"
					/>
				</div>
				<div
					class="sidebar-cart__warning"
					v-if="totalItemsWithoutAdditional > 0 && !isCartValidForCheckout"
					v-html="minCartPriceMessage"
				/>

				<SidebarCartCrossSell
					v-if="isEditableParticipant && !showContinue"
					:cross-sell="cartCrossSell"
					:locked-products="lockedProducts"
					@config="$emit('config', $event)"
					@addToCart="$emit('addToCart', $event)"
					@notify="onNotifyProductAdded"
				/>

				<template v-if="totalItemsWithoutAdditional || (hasFeature('FEATURE_GROUP_ORDERS') && cartIsGroupOrder)">
					<div class="sidebar-cart__tabs">
						<button
							class="sidebar-cart__tab"
							:class="{
								'sidebar-cart__tab--active': activeTab == 'overview'
							}"
							v-html="$t('cart.tabOverview')"
							@click="onTabClick('overview')"
						/>
						<button
              v-if="showInfoTab"
							class="sidebar-cart__tab"
							:class="{
								'sidebar-cart__tab--active': activeTab == 'info'
							}"
							v-html="$t('cart.tabInfo')"
							@click="onTabClick('info')"
						/>
					</div>
					<div
						class="sidebar-cart__tab-content"
						v-if="activeTab == 'overview'"
					>
						<ul class="sidebar-cart__list">
							<template v-if="hasFeature('FEATURE_GROUP_ORDERS') && cartIsGroupOrder">
								<li
									v-for="participant in groupOrderParticipantsEnhanced"
									:key="participant.id"
									class="sidebar-cart__list-participant"
								>
									<div class="sidebar-cart__list-participant-header">
										<SVGSmallCheckmark
											v-if="participant.ready"
											class="sidebar-cart__list-participant-ready"
										/>
										<div
											class="sidebar-cart__list-participant-name"
											v-sanitize="participant.name"
										/>
										<button
											v-if="groupOrderIsOwner || (groupOrderParticipant && participant.id === groupOrderParticipant.id)"
											class="sidebar-cart__list-participant-edit"
											@click="editGroupOrderParticipant(participant)"
										>
											<SVGEdit/>
										</button>
										<button
											v-if="groupOrderIsOwner && !participant.isOwner"
											class="sidebar-cart__list-participant-remove"
											@click="removeGroupOrderParticipant(participant)"
										>
											<SVGClose/>
										</button>
									</div>
									<ul class="sidebar-cart__list">
										<component
											:is="itemType(item)"
											v-for="item in participant.items"
											:key="item.id"
											:item="item"
											:locked="lockedItems[item.id]"
											:editable="isEditableParticipant"
											:restricted="!participant.isCurrentUser && !groupOrderIsOwner"
											:outside="showContinue"
											@sub="onSub(item)"
											@add="onAdd(item)"
											@edit="onChildProductModal(item)"
											@remove="onDelete(item)"
											@copy="onCopy(item)"
											@notes="onNotes(item)"
										/>
										<li
											v-if="!participant.items.length"
											class="sidebar-cart__list-item sidebar-cart__list-item--empty"
											v-html="$t('cart.groupOrder.noItems')"
										/>
									</ul>
								</li>
							</template>
							<template v-else>
								<component
									:is="itemType(item)"
									v-for="item in itemsWithoutAdditional"
									:key="item.id"
									:item="item"
									:locked="lockedItems[item.id]"
									:editable="isEditableParticipant"
									:outside="showContinue"
									@sub="onSub(item)"
									@add="onAdd(item)"
									@edit="onChildProductModal(item)"
									@remove="onDelete(item)"
									@copy="onCopy(item)"
									@notes="onNotes(item)"
								/>
								<li
									v-if="totalItemsWithoutAdditional <= 0"
									class="sidebar-cart__list-item sidebar-cart__list-item--empty"
									v-html="$t('cart.warningCartEmpty')"
								/>
							</template>
						</ul>
						<SidebarCartAdditionalProducts
							v-if="hasFeature('FEATURE_ADDITIONALPRODUCTS') && additionalItemsAvailable && totalItemsWithoutAdditional"
							:additional-items="additionalItems"
							:additional-items-price="additionalItemsPrice"
							:additional-open="additionalOpen"
							@toggle="onAdditionalToggle"
							@edit="onAdditionalEdit"
						/>
					</div>
					<div
            v-if="showInfoTab && activeTab == 'info'"
						class="sidebar-cart__tab-content"
					>
						<SidebarCartItemsInfo v-if="hasFeature('FEATURE_NUTRITION_DETAILS_CART')"/>
						<div
							v-else
							class="sidebar-cart__details"
						>
							<h3
								class="sidebar-cart__details-title"
								v-html="$t('cart.details.tempTitle')"
							/>
							<div
								class="sidebar-cart__details-text"
								v-html="$t('cart.details.tempText')"
							/>
						</div>
					</div>
				</template>
				<template v-else>
					<ul class="sidebar-cart__list">
						<li
							class="sidebar-cart__list-item sidebar-cart__list-item--empty"
							v-html="$t('cart.warningCartEmpty')"
						/>
					</ul>
				</template>
			</div>

			<div
				v-if="totalItemsWithoutAdditional"
				class="sidebar-cart__summary"
			>

				<div
					class="sidebar-cart__summary-subtotal"
					v-if="showSubtotal"
				>
					<div class="sidebar-cart__summary-subtotal-title" v-html="$t('cart.subtotal')" />
					<div
						class="sidebar-cart__summary-subtotal-price"
						v-html="$options.filters.fwgpricecent(subtotal, 'short')"
					/>
				</div>

				<div
					class="sidebar-cart__summary-row"
					v-for="discount in discounts"
					:key="discount.name"
				>
					<div class="sidebar-cart__summary-row-info">
						<div
							class="sidebar-cart__summary-row-info-title"
						>
							<span v-html="discount.name"></span>
							<!-- DEBUG: remove coupon -->
						</div>
					</div>
					<button
						v-if="discount.removable !== false && !promotionCouponLocked && !promotionCouponDisabled"
						class="sidebar-cart__summary-row-remove"
						@click="$store.dispatch('cart/removeCoupon')"
					>
						<SVGClose/>
					</button>
					<div
						class="sidebar-cart__summary-row-price"
						v-html="$options.filters.fwgpricecent(discount.amount.current, 'short')"
					>

					</div>
				</div>
				<!-- DEBUG ADD COUPON -->
				<!--
				<div
					class="sidebar-cart__summary-row"
					v-if="discounts.length <= 0"
				>
					<div class="sidebar-cart__summary-row-info">
						<button @click="$store.dispatch('cart/addCoupon',{coupon: 'test'})">Add Coupon</button>
					</div>
				</div>
				-->
				<div
					v-if="deliveryPrice > 0"
					class="sidebar-cart__summary-row"
					>
					<div class="sidebar-cart__summary-row-info">
						<div class="sidebar-cart__summary-row-info-title" v-html="$t('cart.deliveryFee')" />
						<div
							v-if="minCartFreePrice > 0"
							class="sidebar-cart__summary-row-info-meta"
							v-html="$t('cart.freeDeliveryThreshold', { price: $options.filters.fwgpricecent(minCartFreePrice) })"
						/>
					</div>
					<div
						v-html="$options.filters.fwgpricecent(deliveryPrice, 'short')"
						class="sidebar-cart__summary-row-price"
					/>
				</div>
				<div
					v-else-if="cartIsUberDirect && !cartHasAddress"
					class="sidebar-cart__summary-row"
					>
					<div class="sidebar-cart__summary-row-info">
						<div class="sidebar-cart__summary-row-info-title" v-html="$t('uberDirect.deliveryFeeCheckout')" />
					</div>
				</div>

				<div
					v-if="showCo2 && cartChannelHasCo2Compensation && cartCo2Compensation && cartCo2CompensationAmount"
					class="sidebar-cart__summary-row"
					>
					<div class="sidebar-cart__summary-row-info">
						<div class="sidebar-cart__summary-row-info-title" v-html="$t('cart.co2Compensation')" />
					</div>
					<div
						v-html="$options.filters.fwgpricecent(cartCo2CompensationAmount, 'short')"
						class="sidebar-cart__summary-row-price"
					/>
				</div>

				<div
					v-if="showTip && cartTip"
					class="sidebar-cart__summary-row"
					>
					<div class="sidebar-cart__summary-row-info">
						<div class="sidebar-cart__summary-row-info-title" v-html="$t('cart.tip')" />
					</div>
					<div
						v-html="$options.filters.fwgpricecent(cartTip, 'short')"
						class="sidebar-cart__summary-row-price"
					/>
				</div>

				<div class="sidebar-cart__summary-total">
					<div class="sidebar-cart__summary-total-title" v-html="$t('cart.total')" />
					<div
						class="sidebar-cart__summary-total-price"
						v-html="$options.filters.fwgpricecent(displayTotal, 'short')"
					/>
				</div>

			</div>
			<Button
				v-if="!cartIsGroupOrder || groupOrderIsOwner"
				v-show="totalItemsWithoutAdditional > 0"
				@click.prevent="gotoCheckout"
				:disabled="loading || !isCartValidForCheckout"
				class="sidebar-cart__submit"
				:text="$t('cart.orderButton')"
				level="primary"
				type="button"
			/>
			<Button
				v-else
				v-show="groupOrderParticipantEnhanced && groupOrderParticipantEnhanced.items.length"
				@click.prevent="onParticipantReady"
				:disabled="loading"
				class="sidebar-cart__ready"
				:text="groupOrderParticipantEnhanced && groupOrderParticipantEnhanced.ready ? $t('cart.groupOrder.unReadyButton') : $t('cart.groupOrder.readyButton')"
				level="primary"
				type="button"
			/>
		</div>
		<div
			class="sidebar-cart__backdrop"
			@click="close"
		/>

		<Modal
			v-if="hasFeature('FEATURE_ADDITIONALPRODUCTS')"
			ref="modal"
			overlay-type="additional-products"
			:html-classes="['hide--header', 'show--modal']"
			soft-close
			@close="onModalClose"
			@softclose="onAdditionalEditSoftClose"
		>
			<OverlayAdditionalProducts
				ref="overlayAdditionalProducts"
				@close="onAdditionalEditClose"
			/>
		</Modal>

		<Modal
			ref="timeModal"
			overlay-type="time-change"
			:html-classes="['hide--header', 'show--modal']"
			narrow
			@close="onModalClose"
		>
			<OverlayTimeEdit
				@close="onTimeEditClose"
				@change="onTimeChange"
			/>
		</Modal>

		<Modal
			ref="locationModal"
			overlay-type="location-change"
			:html-classes="['hide--header', 'show--modal']"
			narrow
			@close="onModalClose"
		>
			<OverlayLocationEdit
				@cancel="onLocationEditClose"
				@confirm="onLocationEditConfirm"
			/>
		</Modal>

		<Modal
			ref="chartChangeModal"
			overlay-type="cart-change"
			:html-classes="['hide--header', 'show--modal']"
			narrow
			@close="onModalClose"
		>
			<!-- TODO: the overlay will be opened in an event and title and message will have to be filled somewhere -->
			<OverlayCartChange
				:changed-items="cartChangedItems"
				@confirm="onCartChangeClose"
			/>
		</Modal>

		<Modal
			ref="itemNotesModal"
			overlay-type="cart-item-notes"
			:html-classes="['hide--header', 'show--modal']"
			narrow
			@close="onModalClose"
		>
			<div class="overlay-dialog">
				<form
					class="sidebar-cart__notes-form"
					@submit="onNotesSubmit"
					v-if="notesItem"
				>
					<h2
						class="sidebar-cart__notes-form-title"
						v-html="notesItem.name"
					/>
					<h3
						class="sidebar-cart__notes-form-subtitle"
						v-if="notesItem.variant"
						v-html="notesItem.variant"
					/>
					<FormInputTextarea
						:placeholder="$t('forms.labels.notes')"
						:label="$t('forms.labels.notes')"
						v-model="notesItem.notes"
					/>
					<Button
						:text="$t('cart.notes.saveButton')"
						level="primary"
						type="submit"
					/>
				</form>
			</div>
		</Modal>
		<Modal
			v-if="hasFeature('FEATURE_GROUP_ORDERS')"
			ref="groupOrderModal"
			overlay-type="group-order-edit"
			:html-classes="['hide--header', 'show--modal']"
			v-slot="{ closeModal }"
			narrow
			@close="onModalClose"
		>
			<OverlayGroupOrderForm
				:title="$t('cart.groupOrder.edit.title')"
				:name="groupOrderForm.name"
				:button-text="$t('cart.groupOrder.edit.saveButton')"
				:loading="loading"
				@cancel:grouporder="closeModal"
				@submit:grouporder="onGroupOrderEditSubmit"
			/>
		</Modal>
	</div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import htmlComponent from 'htmlcomponent'
import GenericMixin from '@/mixins/GenericVueMixin';
import OverlayMixin from '@/mixins/OverlayVueMixin';
import CartOrderMixin from '@/mixins/CartOrderVueMixin';
import OverlayAdditionalProducts from '@/pages/OverlayAdditionalProducts'
import OverlayTimeEdit from '@/pages/OverlayTimeEdit';
import OverlayLocationEdit from '@/pages/OverlayLocationEdit';
import OverlayCartChange from '@/pages/OverlayCartChange';
import OverlayGroupOrderForm from '@/pages/OverlayGroupOrderForm'
import SidebarCartItem from '@/components/SidebarCartItem';
import SidebarCartItemConfigurable from '@/components/SidebarCartItemConfigurable';
import SidebarCartAdditionalProducts from '@/components/SidebarCartAdditionalProducts';
import SidebarCartItemsInfo from '@/components/SidebarCartItemsInfo';
import SidebarCartCrossSell from '@/components/SidebarCartCrossSell';
import FormInputTextarea from '@/components/FormInputTextarea';
import Button from '@/components/Button';
import Modal from '@/components/Modal';
import { dayjs, isSameDay, dayjsLocal } from "@/helpers/dayjs"

export default {
	mixins: [
		GenericMixin,
		OverlayMixin,
		CartOrderMixin,
	],
	components: {
		SidebarCartItem,
		SidebarCartItemConfigurable,
		SidebarCartAdditionalProducts,
		SidebarCartItemsInfo,
		SidebarCartCrossSell,
		Button,
		OverlayAdditionalProducts,
		Modal,
		OverlayTimeEdit,
		OverlayLocationEdit,
		OverlayCartChange,
		OverlayGroupOrderForm,
		FormInputTextarea,
	},
	props: {
		isEditable: {
			default: true
		},
		isInline: {
			type: Boolean,
			default() {
				return false;
			},
		},
		overlayType: {
			type: String,
			default() {
				return 'sidebar-cart';
			},
		},
		showCo2: {
			type: Boolean,
			default() {
				return false
			}
		},
		showTip: {
			type: Boolean,
			default() {
				return false
			}
		},
		lockedProducts: {
			type: Object,
			default() {
				return {}
			}
		}
	},
	data() {
		return {
			activeTab: 'overview',
			lockedItems: {},
			overlayIgnoreScrollPos: true,
			additionalOpen: false,
			editItem: null,
			editItemSelectedIds: [],
			overlaysAllowed: [
				'additional-products',
				'childproducts',
				'time-change',
				'location-change',
				'cart-change',
				'cart-item-notes',
				'group-order-edit',
			],
			modalCloseParent: false,
			notesItem: null,
			groupOrderForm: {
				id: '',
				token: null,
				name: ''
			},
		};
	},
	computed: {
		...mapGetters("cart", [
			"cartDeliveryTime",
			"subtotal",
			"total",
			"totalWithoutTip",
			"totalWithoutSurcharges",
			"totalWithoutCo2",
			"totalItemsWithoutAdditional",
			"isCartValidForCheckout",
			"discounts",
			"deliveryPrice",
			"deliveryTime",
			"minCartPrice",
			"minCartPriceTime",
			"minCartFreePrice",
			"shippingMethod",
			"shopLocation",
			"cartChangedItems",
			"cartOrderNow",
			"cartTipEnabled",
			"cartTip",
			"cartChannelHasCo2Compensation",
			"cartCo2Compensation",
			"cartCo2CompensationAmount",
			"showContinue",
			"orderStates",
			"itemsWithoutAdditional",
			"cartCrossSell",
			"cartOutdoorDeliveryPoint",
			"cartZip",
			"cartIsGroupOrder",
			"cartIsGroupOrderLocked",
			"tokenValue",
      "cartIsUberDirect",
			"orderType",
      "cartHasAddress"
		]),
		...mapGetters("additionalproducts", [
			"additionalItems",
			"additionalItemsPrice",
			"additionalItemsAvailable",
		]),
		...mapGetters("childproducts", [
			"childProducts"
		]),
		...mapGetters('grouporder', [
			'groupOrderParticipant',
			'groupOrderParticipantReady',
			'groupOrderIsOwner',
			'groupOrderParticipants',
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value)
				this.$emit('loading', value)
			},
			get() {
				return this.isLoading
			}
		},
		isEditableEnhanced() {
			return this.isEditable && !(this.hasFeature('FEATURE_GROUP_ORDERS') && this.cartIsGroupOrder && this.cartIsGroupOrderLocked)
		},
		isEditableParticipant() {
			return this.isEditable && !(this.hasFeature('FEATURE_GROUP_ORDERS') && this.cartIsGroupOrder && (this.cartIsGroupOrderLocked || this.groupOrderParticipantReady))
		},
		deliveryTimeEnhanced() {
			return this.cartOrderNow
				? this.$t('cart.orderNowTime', { minutes: this.deliveryTime })
				: this.cartDeliveryTime
					? `${this.cartDeliveryTime.format('L')} | ${this.cartDeliveryTime.format('LT')}`
					: '';
		},
		displayTotal() {
			if (!this.showCo2 && !this.showTip) {
				return this.totalWithoutSurcharges;
			}
			else if (this.showCo2 && !this.showTip) {
				return this.totalWithoutTip
			}
			else if (!this.showCo2 && this.showTip) {
				return this.totalWithoutCo2
			}
			else {
				return this.total
			}
		},
		showSubtotal() {
			return this.subtotal != this.displayTotal || !!this.discounts.length || !!this.cartTip;
		},
		displayLocation() {
			return !this.isDelivery
				? this.shopLocation && this.shopLocation.name
				: this.cartOutdoorDeliveryPoint && this.cartOutdoorDeliveryPoint.title || this.cartZip?.zip
		},
		minCartPriceMessage() {
			let data = {
				price: this.$options.filters.fwgpricecent(this.minCartPrice, 'html')
			}
			let key = 'cart.warningMinimumAmount'

			if (this.minCartPriceTime) {
				key = 'cart.warningMinimumAmountTimed'
				data = {
					...data,
					...this.minCartPriceTime
				}
			}
			return this.$t(key, data)
		},
		groupOrderParticipantsEnhanced() {
			return this.groupOrderParticipants.map(participant => {
				return {
					...participant,
					isCurrentUser: participant.id === this.groupOrderParticipant?.id,
					items: this.itemsWithoutAdditional.filter(item =>
						(item.groupOrderParticipantId === participant.id) ||
						(!item.groupOrderParticipantId && participant.isOwner)
					)
				}
			})
		},
		groupOrderParticipantEnhanced() {
			return this.groupOrderParticipantsEnhanced.find(p => p.isCurrentUser)
		},
    showInfoTab() {
      return !this.isOrderTypeDonation
    }
	},
	watch: {
		cartChangedItems(changeItems) {
			if (!this.$refs.timeModal.visible && changeItems.length) {
				this.$refs.chartChangeModal.open();
			}
		}
	},
	methods: {
		backToShop() {
			// TODO: this should be done with a proper redirect
			this.$store.dispatch('redirect', {name: 'shop'})
		},
		itemType(item) {
			return item.isEditable ? 'SidebarCartItemConfigurable' : 'SidebarCartItem';
		},
		onTabClick(tab) {
			this.activeTab = tab;
		},
		async onAdd(item) {
			Vue.set(this.lockedItems, item.id, true);
			try {
				await this.$store.dispatch("cart/updateItem", { identifier: item.id, quantity: item.quantity + 1 })
			}
			catch(err) {
				this.handleErrorDialog(err, {
					title: err?.response?.data?.errors?.availability ? this.$t('shoplist.product.errors.availability') : this.$t('forms.errors.error'),
					message: err?.response?.data?.errors?.availability?.join('<br>') || this.$t('forms.errors.generic'),
					confirm: 'OK',
          callback: () => this.onAdd(item)
				});
			}
			Vue.set(this.lockedItems, item.id, false);
		},
		async onSub(item) {
			Vue.set(this.lockedItems, item.id, true);
			try {
				await this.$store.dispatch("cart/updateItem", { identifier: item.id, quantity: item.quantity - 1 })
			}
			catch(err) {
				this.handleErrorDialog(err, {
					title: err?.response?.data?.errors?.availability ? this.$t('shoplist.product.errors.availability') : this.$t('forms.errors.error'),
					message: err?.response?.data?.errors?.availability?.join('<br>') || this.$t('forms.errors.generic'),
					confirm: 'OK',
          callback: () => this.onSub(item)
				});
			}
			Vue.set(this.lockedItems, item.id, false);
		},
		async onDelete(item) {
			Vue.set(this.lockedItems, item.id, true);
			try {
				await this.$store.dispatch("cart/updateItem", { identifier: item.id, quantity: 0 })
			}
			catch(err) {
				this.handleErrorDialog(err, {
					title: err?.response?.data?.errors?.availability ? this.$t('shoplist.product.errors.availability') : this.$t('forms.errors.error'),
					message: err?.response?.data?.errors?.availability?.join('<br>') || this.$t('forms.errors.generic'),
					confirm: 'OK',
          callback: () => this.onDelete(item)
				});
			}
			Vue.set(this.lockedItems, item.id, false);
		},
		async onCopy(item) {
			try {
				await this.$store.dispatch('cart/copyItem', { identifier: item.id })
			}
			catch(err) {
				this.handleErrorDialog(err, {
					title: err?.response?.data?.errors?.availability ? this.$t('shoplist.product.errors.availability') : this.$t('forms.errors.error'),
					message: err?.response?.data?.errors?.availability?.join('<br>') || this.$t('forms.errors.generic'),
					confirm: 'OK',
          callback: () => this.onCopy(item)
				});
			}
		},
		onNotes(item) {
			this.notesItem = item;
			this.$refs.itemNotesModal.open();
		},
		async onNotesSubmit() {
			this.$refs.itemNotesModal.close();
			Vue.set(this.lockedItems, this.notesItem.id, true);
			try {
				await this.$store.dispatch("cart/updateItemNotes", { identifier: this.notesItem.id, notes: this.notesItem.notes })
			}
			catch(err) {
				this.handleErrorDialog(err, {
          callback: () => this.onNotesSubmit()
				});
			}
			Vue.set(this.lockedItems, this.notesItem.id, false);
		},
		close() {
			htmlComponent.send('overlay:close', {type: this.overlayType});
		},
		async gotoCheckout() {
			if (this.totalItemsWithoutAdditional > 0) {
				if (this.cartIsGroupOrder && !this.groupOrderIsOwner) {
					return;
				}
				else if (this.cartIsGroupOrder && this.groupOrderIsOwner) {
					if (!this.groupOrderParticipants.filter(p => !p.isOwner).every(p => p.ready)) {
						const confirmed = await this.$store.dispatch('dialog/showDialog', {
							title: this.$t('cart.groupOrder.ready.dialogTitle'),
							message: this.$t('cart.groupOrder.ready.dialogText'),
							confirm: this.$t('cart.groupOrder.ready.dialogConfirm'),
							cancel: this.$t('cart.groupOrder.ready.dialogCancel'),
						})
						if (!confirmed) {
							return;
						}
					}

					this.loading = true;
					await this.$store.dispatch('cart/lockGroupOrder', { locked: true });
					// this.loading = false;
				}
				this.$store.dispatch('redirect', {name: "checkout"})
			}
		},

		onTimeEdit(closeParent = false) {
			this.$refs.timeModal.open();
			this.modalCloseParent = closeParent;
		},
		onTimeEditClose() {
			this.$refs.timeModal.close();
			this.onModalClose();
		},
		onModalClose() {
			if (this.modalCloseParent) {
				htmlComponent.send('overlay:close', {type: this.overlayType});
				this.modalCloseParent = false;
			}
		},
		onTimeChange(form) {
			// TODO: check if something has to be done here
			this.$refs.timeModal.close();
		},

		onLocationEdit() {
			this.$refs.locationModal.open();
		},
		onLocationEditClose() {
			this.$refs.locationModal.close();
		},
		onLocationEditConfirm() {
			// this.$store.dispatch('redirect', {name: 'home'});
			htmlComponent.send('overlay:open', {type: 'overlay-orderbox'});
		},

		onAdditionalEdit() {
			this.$refs.modal.open();
		},
		onAdditionalEditSoftClose() {
			this.$refs.overlayAdditionalProducts.onClose();
		},
		onAdditionalEditClose() {
			this.$refs.modal.close();
		},
		onAdditionalToggle() {
			this.additionalOpen = !this.additionalOpen;
		},
		onNotifyProductAdded(item) {
			if (item.isEditable && !item.isConfigurable) {
				this.onChildProductModal(item)
			}
		},
		onChildProductModal(editItem) {
			htmlComponent.send('overlay:open', {type: 'overlay-childproducts', item: editItem, selectedCodes: editItem.children.map(v => v.code)});
		},
		onCartChangeClose() {
			this.$refs.chartChangeModal.close();
			this.$store.dispatch('cart/clearCartChanges');
		},
		async editGroupOrderParticipant(participant) {
			this.groupOrderForm.id = participant.id;
			this.groupOrderForm.name = participant.name;
			this.groupOrderForm.token = this.tokenValue;
			this.$refs.groupOrderModal.open();
		},
		async removeGroupOrderParticipant(participant) {
			const confirmed = await this.$store.dispatch('dialog/showDialog', {
				title: this.$t('cart.groupOrder.remove.dialogTitle'),
				message: this.$t('cart.groupOrder.remove.dialogText', participant),
				confirm: this.$t('cart.groupOrder.remove.dialogConfirm'),
				cancel: this.$t('cart.groupOrder.remove.dialogCancel'),
			})
			if (confirmed) {
				this.loading = true;
				try {
					await this.$store.dispatch('grouporder/removeParticipant', { token: this.tokenValue, id: participant.id });
				}
				catch(err) {
					this.handleErrorDialog(err, {
            callback: () => this.removeGroupOrderParticipant(participant)
          });
				}
				this.loading = false;
			}
		},
		async onGroupOrderEditSubmit({name}) {
			this.loading = true
			if (name) {
				this.groupOrderForm.name = name
				try {
					this.$refs.groupOrderModal.close()
					try {
						await this.$store.dispatch('grouporder/editParticipant', this.groupOrderForm);
					}
					catch(err) {
						this.handleErrorDialog(err, {
              callback: () => this.onGroupOrderEditSubmit({name})
            });
					}
				}
				catch (err) {
					this.$refs.groupOrderModal.open()
					this.isDebug() && console.error(err)
				}
			}
			this.loading = false
		},
		async onParticipantReady() {
			this.loading = true
			if (this.groupOrderParticipant) {
				try {
					try {
						await this.$store.dispatch('grouporder/participantReady', {
							token: this.tokenValue,
							id: this.groupOrderParticipant?.id,
							ready: !this.groupOrderParticipantReady,
						})
					}
					catch (err) {
						this.handleErrorDialog(err, {
              callback: () => this.onParticipantReady()
            });
					}
				}
				catch (err) {
					this.$refs.groupOrderModal.open()
					this.isDebug() && console.error(err)
				}
			}
			this.loading = false
		},
		beforeOverlayOpen() {
			this.activeTab = 'overview';
		},
	}
}
</script>

<style>

</style>
